/**
 * Port of app/lib/mortgage_detail.py. Field names stay snake_case so the
 * shapes still match src/types.ts and the backend's JSON, which keeps the
 * Python-vs-TypeScript oracle diff a plain comparison.
 */

import { QUOTED_RATES, REVERT_TO_RATE } from './rates.ts';

/**
 * Values match Python's `auto()` numbering, which is what the API serialises.
 *
 * A const object rather than a TS `enum` because node's type stripping rejects
 * enums outright — they need code generation, and the ported tests run straight
 * through node with no build step.
 */
export const Bank = {
    HSBC: 1,
    HALIFAX: 2,
    LLOYDS: 3,
    NATIONWIDE: 4,
    BARCLAYS: 5,
    EMPTY: 6,
    /**
     * Not a lender: the average of what lenders were advertising. Added after
     * the Python, which is why it is on the end rather than in order.
     */
    MARKET: 7,
} as const;

export type Bank = (typeof Bank)[keyof typeof Bank];

const BANK_NAMES = Object.fromEntries(
    Object.entries(Bank).map(([bank_name, value]) => [value, bank_name]),
) as Record<Bank, string>;

export interface MortgageDetail {
    bank: Bank;
    fixed_term: number;
    initial_interest: number;
    subsequent_interest: number;
    setup_fee: number;
    maximum_loan_to_value: number;
}

export class TooHighLoanToValueError extends Error {}

export function mortgageDetail(
    bank: Bank,
    fixed_term: number,
    initial_interest: number,
    subsequent_interest: number,
    setup_fee: number,
    maximum_loan_to_value = 0.0,
): MortgageDetail {
    return {
        bank,
        fixed_term,
        initial_interest,
        subsequent_interest,
        setup_fee,
        maximum_loan_to_value,
    };
}

export function name(detail: MortgageDetail) {
    return (
        `${BANK_NAMES[detail.bank]}: ${detail.fixed_term} @ ` +
        `${(detail.initial_interest * 100).toFixed(2)}, ` +
        `${(detail.subsequent_interest * 100).toFixed(2)}, ` +
        `£${detail.setup_fee}`
    );
}

export function loanToValue(deposit: number, house_price: number) {
    return (house_price - deposit) / house_price;
}

export function bestMortgageDetails(all_mortgage_details: MortgageDetail[]) {
    return all_mortgage_details.reduce((best, detail) =>
        // Strictly less-than keeps the first of equal rates, matching min().
        detail.initial_interest < best.initial_interest ? detail : best,
    );
}

export function bestMortgageDetailsForLtv(
    all_mortgage_details: MortgageDetail[],
    deposit: number,
    house_price: number,
) {
    const ltv = loanToValue(deposit, house_price);
    const available = all_mortgage_details.filter(
        (detail) => detail.maximum_loan_to_value >= ltv,
    );

    if (available.length === 0) {
        throw new TooHighLoanToValueError(
            `No available mortgage detail for LTV of ${formatPercent(ltv)}`,
        );
    }

    return bestMortgageDetails(available);
}

/** Matches Python's `{:.0%}` so error messages compare equal in the oracle. */
function formatPercent(value: number) {
    return `${Math.round(value * 100)}%`;
}

/**
 * What the market was advertising, as products this app can shop for.
 *
 * These were four hardcoded Barclays deals with a £899 fee, from whenever
 * somebody last looked. They are now generated from the Bank of England's
 * quoted rates by `./scripts/rates` — nine bands rather than four, a
 * revert-to-rate that is measured rather than assumed, and no fee, because a
 * market average has none to quote. Nothing here charges the fee anyway; it
 * only ever appeared in `name()`.
 */
export const realLifeMortgageDetails: MortgageDetail[] = QUOTED_RATES.map(
    (quoted) =>
        mortgageDetail(
            Bank.MARKET,
            quoted.fixed_term,
            quoted.initial_interest,
            REVERT_TO_RATE,
            0,
            quoted.maximum_loan_to_value,
        ),
);

/** The band most people are actually in, and the one to quote as typical. */
const TYPICAL_LOAN_TO_VALUE = 0.75;

/**
 * A realistic mortgage to start a page from, rather than the cheapest one going
 * — the term comparison page fills its rate fields from this, and the best rate
 * on the board is a 60% LTV deal most people cannot have.
 */
export function typicalMortgageDetail(fixed_term: number) {
    return (
        realLifeMortgageDetails.find(
            (detail) =>
                detail.fixed_term === fixed_term &&
                detail.maximum_loan_to_value === TYPICAL_LOAN_TO_VALUE,
        ) ?? realLifeMortgageDetails[0]
    );
}

export function getBestMortgageDetail(deposit: number, house_price: number) {
    return bestMortgageDetailsForLtv(
        realLifeMortgageDetails,
        deposit,
        house_price,
    );
}
